import React, { useCallback } from "react";
import { Card, CardBody } from "@ddm-design-system/card";
import { Icon } from "@ddm-design-system/icon";
import { BodyHighlight, DescriptionMedium } from "@ddm-design-system/typography";
import { IX10 } from "../../../store/installation/types";
import { X10_KEG_TIME_LIMIT_DAYS } from "../../../constants";
import { ReactComponent as X10Icon } from "../../../assets/x10/x10.svg";
import X10Keg from "./X10Keg";
import X10Badges from "./X10Badges";
import useX10Text from "./useX10Text";
import { X10_CARD_CLASSES, formatTemperature, x10BeverageLabel } from "./x10Presentation";

interface IProps {
  x10: IX10;
  onClick: (x10: IX10) => void;
}

export const X10Card: React.FC<IProps> = ({ x10, onClick }) => {
  const t = useX10Text();
  const handleClick = useCallback(() => onClick(x10), [x10, onClick]);
  const { evaluation } = x10;
  const showMetrics = !x10.offline && !evaluation.open;

  return (
    <Card
      className={`bg-white !rounded-[4px] box-border !border-[3px] w-[240px] h-[310px] relative cursor-pointer hover:z-[1] xs:w-full xs:max-w-none ${
        X10_CARD_CLASSES[x10.status]
      }`}
      onClick={handleClick}
    >
      <CardBody className="h-full box-border !p-md">
        {/* Card doesn't pass data attributes through */}
        <div className="flex flex-col items-center h-full" data-test-id="x10-card">
          <div className="flex items-center justify-center w-full mb-md gap-xs min-w-0">
            <X10Icon className="flex-shrink-0 w-[10px] h-[20px] text-black" aria-hidden="true" />
            <BodyHighlight className="truncate">{x10.name}</BodyHighlight>
          </div>
          <X10Keg x10={x10} />
          <DescriptionMedium className="mt-sm w-full truncate text-center">
            {x10BeverageLabel(x10, t)}
          </DescriptionMedium>
          <div className="flex items-center justify-center gap-md mt-xxs h-[20px] text-grey-grey100">
            {showMetrics && x10.vesselTemperature !== undefined && (
              <span
                className={`flex items-center gap-xxs ${
                  evaluation.highTemperature ? "text-alert-alert100" : ""
                }`}
                data-test-id="x10-card-temperature"
              >
                <Icon name="Temperature" size={14} className="fill-current" />
                <DescriptionMedium>{formatTemperature(x10.vesselTemperature)}</DescriptionMedium>
              </span>
            )}
            {showMetrics && evaluation.daysOnKeg !== undefined && (
              <span
                className={`flex items-center gap-xxs ${
                  evaluation.kegTime === "danger"
                    ? "text-alert-alert100"
                    : evaluation.kegTime === "warning"
                    ? "text-warning-warning100"
                    : ""
                }`}
                data-test-id="x10-card-keg-time"
              >
                <Icon name="Sleep" size={14} className="fill-current" />
                <DescriptionMedium>
                  {t("day_of", { DAYS: evaluation.daysOnKeg, LIMIT: X10_KEG_TIME_LIMIT_DAYS })}
                </DescriptionMedium>
              </span>
            )}
          </div>
          <div className="flex justify-around mt-auto w-full h-lg items-center">
            <X10Badges x10={x10} />
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default X10Card;
